/**
 * Tab „Krank“ (/urlaub): Krankmeldungsliste mit Typ-Badge + AU-Status
 * (fehlt/eingereicht), Bradford-Faktor bei Filter Mitarbeiter+Jahr,
 * Melden-Dialog (Self-Service), AU-Upload (multipart) und
 * Gesundmelden/Schließen-Dialog (Gate personal_schreiben) — AP 3.4,
 * Vorbild netzfabriks-erp SickLeavePage.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, CheckCircle2, FileUp, Info, Plus } from "lucide-react"
import { toast } from "sonner"
import { BusinessDayRangePicker, LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { fmtDate, fmtDays } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type { SickLeave, SickLeaveType, VacationOverlap } from "@/types"
import { SICK_TYPE_BADGE, SICK_TYPE_LABEL, SICK_TYPE_VALUES } from "./labels"

/** 400ms-Debounce fuer die Urlaubs-Vorschau (weniger Requests beim Tippen) */
function useDebounced<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(t)
  }, [value, delay])
  return debounced
}

/** „13.–19.07.“ — kompakter Bereich; Jahr nur einmal am Ende */
function fmtRange(from: string, to: string): string {
  const f = fmtDate(from)
  const t = fmtDate(to)
  return from === to ? f : `${f} – ${t}`
}

/** Menschlich lesbare Vorschau: „Urlaub 13.–24.07. → 13.–19.07. + 23.–24.07.“ */
function overlapPreviewText(o: VacationOverlap): string {
  const original = fmtRange(o.vacationFrom, o.vacationTo)
  if (o.resultingRanges.length === 0) {
    return `Urlaub ${original} → entfällt komplett`
  }
  const parts = o.resultingRanges.map((r) => fmtRange(r.fromDate, r.toDate)).join(" + ")
  return `Urlaub ${original} → ${parts}`
}

// ---------------------------------------------------------------------------
// Melden-Dialog
// ---------------------------------------------------------------------------

function ReportSickLeaveDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const canWrite = useCan("personal_schreiben")
  const [employeeId, setEmployeeId] = useState("")
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [sickType, setSickType] = useState<SickLeaveType>("SHORT")
  // Zustimmung zum Auto-Split; nach Erfolg AU-Upload anbieten (Ziel-sickLeaveId)
  const [adjustVacation, setAdjustVacation] = useState(true)
  const [certTarget, setCertTarget] = useState<string | null>(null)
  const certInputRef = useRef<HTMLInputElement>(null)

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
    enabled: open,
  })

  function reset() {
    setEmployeeId("")
    setFrom("")
    setTo("")
    setSickType("SHORT")
    setAdjustVacation(true)
    setCertTarget(null)
  }

  // --- Urlaubs-Vorschau: debounced im Hintergrund, sobald Zeitraum steht ------
  const debFrom = useDebounced(from)
  const debTo = useDebounced(to)
  const debEmployee = useDebounced(employeeId)
  const rangeReady = debFrom !== "" && debTo !== "" && debFrom <= debTo
  const preview = useQuery({
    queryKey: ["sick-leaves", "vacation-preview", debEmployee, debFrom, debTo],
    queryFn: () =>
      api.sickLeaves.previewVacationAdjustment({
        employeeId: debEmployee ? Number(debEmployee) : null,
        fromDate: debFrom,
        toDate: debTo,
      }),
    enabled: open && rangeReady && certTarget === null,
  })
  const plan = preview.data
  const hasOverlap = (plan?.overlaps.length ?? 0) > 0
  const otherConflicts = plan?.hasConflictsOtherThanVacation ?? []
  const hasOtherConflicts = otherConflicts.length > 0

  const onCreated = () => {
    queryClient.invalidateQueries({ queryKey: ["sick-leaves"] })
    queryClient.invalidateQueries({ queryKey: ["absences"] })
    queryClient.invalidateQueries({ queryKey: ["leave"] })
  }

  // Normale Krankmeldung (kein Urlaub im Zeitraum ODER Auto-Split abgewählt)
  const report = useMutation({
    mutationFn: () =>
      api.sickLeaves.create({
        employeeId: employeeId ? Number(employeeId) : null,
        startDate: from,
        endDate: to,
        sickType,
      }),
    onSuccess: () => {
      onCreated()
      toast.success("Krankmeldung erfasst")
      onOpenChange(false)
      reset()
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // Ein-Klick: Urlaub auftrennen + Krankheit buchen (BUrlG §9)
  const reportDuringVacation = useMutation({
    mutationFn: () =>
      api.sickLeaves.reportDuringVacation({
        employeeId: employeeId ? Number(employeeId) : null,
        fromDate: from,
        toDate: to,
        sickType,
      }),
    onSuccess: (res) => {
      onCreated()
      const days = fmtDays(res.vacationDaysReturned)
      const rest = fmtDays(res.accountAfter.remainingDays)
      toast.success(
        `Krankheit eingetragen, ${days} Urlaubstage gutgeschrieben (Konto: ${rest} Tage)`,
      )
      // Bleibt offen für AU-Upload; Formularfelder bleiben bis zum Schließen stehen
      setCertTarget(res.sickLeaveId)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const certUpload = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      api.sickLeaves.uploadCertificate(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sick-leaves"] })
      toast.success("AU-Bescheinigung hochgeladen")
      onOpenChange(false)
      reset()
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function onCertSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file && certTarget) certUpload.mutate({ id: certTarget, file })
    e.target.value = ""
  }

  const valid = from !== "" && to !== "" && from <= to
  const pending = report.isPending || reportDuringVacation.isPending
  const useVacationFlow = hasOverlap && adjustVacation && !hasOtherConflicts
  // Bestätigen sperren, solange eine Fremd-Abwesenheit die Auflösung blockiert
  const submitDisabled =
    !valid || pending || (hasOverlap && hasOtherConflicts && adjustVacation)

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Krankmeldung erfassen</DialogTitle>
          <DialogDescription>
            Erzeugt eine Abwesenheit „Krank“ (gebucht) — AU-Bescheinigung kann danach
            hochgeladen werden.
          </DialogDescription>
        </DialogHeader>

        {/* Nach Ein-Klick-Ausführung: nur noch AU-Upload anbieten */}
        {certTarget ? (
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
              <div>
                <p className="font-medium">Krankheit eingetragen, Urlaub angepasst.</p>
                <p className="mt-0.5">
                  Bitte Krankenschein (AU) hochladen — bei Krankheit im Urlaub ist eine
                  Bescheinigung erforderlich (§ 9 BUrlG).
                </p>
              </div>
            </div>
            <input
              ref={certInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={onCertSelected}
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false)
                  reset()
                }}
              >
                Später hochladen
              </Button>
              <Button
                disabled={certUpload.isPending}
                onClick={() => certInputRef.current?.click()}
              >
                <FileUp /> AU hochladen
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="grid gap-4 py-2">
              <div className="space-y-1.5">
                <Label>Mitarbeiter</Label>
                <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                  <option value="">Ich selbst (verknüpfter Mitarbeiter)</option>
                  {canWrite &&
                    (employees.data?.data ?? []).map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.displayName}
                      </option>
                    ))}
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Von *</Label>
                  <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Bis (voraussichtlich) *</Label>
                  <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </div>
              </div>
              <BusinessDayRangePicker
                from={from}
                to={to}
                onChange={(f, t) => {
                  setFrom(f)
                  setTo(t)
                }}
              />
              <div className="space-y-1.5">
                <Label>Typ *</Label>
                <Select
                  value={sickType}
                  onChange={(e) => setSickType(e.target.value as SickLeaveType)}
                >
                  {SICK_TYPE_VALUES.map((t) => (
                    <option key={t} value={t}>
                      {SICK_TYPE_LABEL[t]}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Fremd-Abwesenheit im Zeitraum: blockiert die Auflösung */}
              {rangeReady && hasOtherConflicts && (
                <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                  <div>
                    <p className="font-medium">
                      Im Zeitraum liegt bereits eine andere Abwesenheit (z. B. Krankheit).
                    </p>
                    <p className="mt-0.5">
                      Bitte zuerst dort korrigieren — dann kann der Urlaub sauber aufgelöst
                      werden.
                    </p>
                    <ul className="mt-1.5 list-disc pl-5">
                      {otherConflicts.map((c) => (
                        <li key={c.absenceId}>
                          {c.reason} ({c.status}): {fmtRange(c.conflictFrom, c.conflictTo)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Urlaub im Zeitraum erkannt: ruhige Info-Box + Vorschau */}
              {rangeReady && hasOverlap && !hasOtherConflicts && plan && (
                <div className="space-y-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
                  <div className="flex items-start gap-2">
                    <Info className="mt-0.5 size-4 shrink-0 text-blue-600" />
                    <p>
                      Dieser Zeitraum liegt im genehmigten/gebuchten Urlaub. Bei einer
                      Krankmeldung mit AU werden die betroffenen Urlaubstage{" "}
                      <strong>nicht angerechnet</strong> (§ 9 BUrlG).
                    </p>
                  </div>
                  <ul className="space-y-1 pl-6">
                    {plan.overlaps.map((o) => (
                      <li key={o.absenceId} className="tabular-nums">
                        {overlapPreviewText(o)}
                      </li>
                    ))}
                  </ul>
                  <p className="pl-6 font-semibold text-blue-900">
                    {fmtDays(plan.vacationDaysReturned)} Urlaubstage werden gutgeschrieben.
                  </p>
                  <div className="flex items-center gap-2 pl-6">
                    <Switch checked={adjustVacation} onCheckedChange={setAdjustVacation} />
                    <Label className="font-normal">
                      Urlaub automatisch anpassen und Krankheit eintragen
                    </Label>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false)
                  reset()
                }}
              >
                Abbrechen
              </Button>
              <Button
                disabled={submitDisabled}
                onClick={() =>
                  useVacationFlow ? reportDuringVacation.mutate() : report.mutate()
                }
              >
                {useVacationFlow ? "Urlaub anpassen & melden" : "Melden"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Gesundmelden/Schließen-Dialog
// ---------------------------------------------------------------------------

function CloseSickLeaveDialog({
  sickLeave,
  onClose,
}: {
  sickLeave: SickLeave
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [actualEnd, setActualEnd] = useState("")

  const close = useMutation({
    mutationFn: () =>
      api.sickLeaves.close(sickLeave.id, actualEnd ? { actualEndDate: actualEnd } : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sick-leaves"] })
      queryClient.invalidateQueries({ queryKey: ["absences"] })
      toast.success("Krankmeldung geschlossen (gesund gemeldet)")
      onClose()
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Gesundmelden / Krankmeldung schließen</DialogTitle>
          <DialogDescription>
            Zeitraum {fmtDate(sickLeave.startDate)} – {fmtDate(sickLeave.endDate)}. Ein
            tatsächliches Ende kürzt die gebuchte Abwesenheit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label>Tatsächliches Ende (optional)</Label>
          <Input type="date" value={actualEnd} onChange={(e) => setActualEnd(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={close.isPending} onClick={() => close.mutate()}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export function SickLeavesTab() {
  const canWrite = useCan("personal_schreiben")
  const queryClient = useQueryClient()
  const currentYear = new Date().getFullYear()

  const [employeeFilter, setEmployeeFilter] = useState("")
  const [yearFilter, setYearFilter] = useState(String(currentYear))
  const [typeFilter, setTypeFilter] = useState<SickLeaveType | "">("")
  const [openOnly, setOpenOnly] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [closeTarget, setCloseTarget] = useState<SickLeave | null>(null)

  // AU-Upload: ein verstecktes File-Input, Ziel-ID im Ref
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadTargetRef = useRef<string | null>(null)

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
  })
  const employeeName = (id: string) =>
    employees.data?.data.find((e) => e.id === id)?.displayName ?? `Mitarbeiter #${id}`

  const params = {
    size: 100,
    employee: employeeFilter ? Number(employeeFilter) : undefined,
    year: yearFilter ? Number(yearFilter) : undefined,
    sickType: typeFilter || undefined,
    openOnly: openOnly || undefined,
  }
  const query = useQuery({
    queryKey: ["sick-leaves", params],
    queryFn: () => api.sickLeaves.list(params),
    placeholderData: keepPreviousData,
  })
  const rows = query.data?.data ?? []
  const bradford = query.data?.bradfordFactor ?? null

  const upload = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      api.sickLeaves.uploadCertificate(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sick-leaves"] })
      toast.success("AU-Bescheinigung hochgeladen")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function startUpload(id: string) {
    uploadTargetRef.current = id
    fileInputRef.current?.click()
  }

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    const id = uploadTargetRef.current
    if (file && id) upload.mutate({ id, file })
    e.target.value = ""
    uploadTargetRef.current = null
  }

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<SickLeave>[]>(
    () => [
      {
        id: "employee",
        header: "Mitarbeiter",
        accessorFn: (s) => employeeName(s.employeeId),
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "period",
        header: "Zeitraum",
        // ISO-Startdatum sortiert chronologisch korrekt
        accessorFn: (s) => s.startDate,
        cell: ({ row }) => (
          <>
            {fmtDate(row.original.startDate)} – {fmtDate(row.original.endDate)}
          </>
        ),
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "sickType",
        header: "Typ",
        accessorFn: (s) => SICK_TYPE_LABEL[s.sickType],
        cell: ({ row }) => (
          <Badge variant="outline" className={SICK_TYPE_BADGE[row.original.sickType]}>
            {SICK_TYPE_LABEL[row.original.sickType]}
          </Badge>
        ),
      },
      {
        id: "certificate",
        header: "AU-Bescheinigung",
        accessorFn: (s) =>
          s.certificateSubmittedAt
            ? `eingereicht ${fmtDate(s.certificateSubmittedAt)}`
            : s.sickType === "WITH_CERTIFICATE"
              ? "fehlt"
              : "nicht erforderlich",
        cell: ({ row }) => {
          const s = row.original
          return s.certificateSubmittedAt ? (
            <Badge variant="outline" className="border-green-300 bg-green-100 text-green-800">
              eingereicht {fmtDate(s.certificateSubmittedAt)}
            </Badge>
          ) : s.sickType === "WITH_CERTIFICATE" ? (
            <Badge variant="outline" className="border-red-300 bg-red-100 text-red-800">
              fehlt
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">nicht erforderlich</span>
          )
        },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (s) => (s.closedAt ? `geschlossen ${fmtDate(s.closedAt)}` : "offen"),
        cell: ({ row }) => {
          const s = row.original
          return s.closedAt ? (
            <span className="text-xs text-muted-foreground">
              geschlossen {fmtDate(s.closedAt)}
            </span>
          ) : (
            <Badge variant="outline" className="border-yellow-300 bg-yellow-100 text-yellow-800">
              offen
            </Badge>
          )
        },
      },
      {
        id: "aktionen",
        header: "Aktionen",
        enableSorting: false,
        enableHiding: false,
        meta: { headClassName: "text-right", cellClassName: "text-right" },
        cell: ({ row }) => {
          const s = row.original
          return (
            <div className="flex justify-end gap-1">
              {!s.closedAt && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={upload.isPending}
                    onClick={() => startUpload(s.id)}
                  >
                    <FileUp /> AU hochladen
                  </Button>
                  {canWrite && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setCloseTarget(s)}
                    >
                      <CheckCircle2 /> Gesundmelden
                    </Button>
                  )}
                </>
              )}
            </div>
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps — employeeName/startUpload sind stabil genug (Daten via employees.data)
    [employees.data, canWrite, upload.isPending],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {/* Filterleiste */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56 space-y-1.5">
            <Label>Mitarbeiter</Label>
            <Select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
              <option value="">Alle Mitarbeiter</option>
              {(employees.data?.data ?? []).map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.displayName}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-32 space-y-1.5">
            <Label>Jahr</Label>
            <Select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              <option value="">Alle Jahre</option>
              {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-44 space-y-1.5">
            <Label>Typ</Label>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as SickLeaveType | "")}
            >
              <option value="">Alle Typen</option>
              {SICK_TYPE_VALUES.map((t) => (
                <option key={t} value={t}>
                  {SICK_TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex h-9 items-center gap-2">
            <Switch checked={openOnly} onCheckedChange={setOpenOnly} />
            <Label className="font-normal">Nur offene</Label>
          </div>
        </div>
        <Button onClick={() => setReportOpen(true)}>
          <Plus /> Krank melden
        </Button>
      </div>

      {/* Bradford-Faktor (nur bei Filter Mitarbeiter+Jahr geliefert) */}
      {bradford !== null && employeeFilter && yearFilter && (
        <p className="text-sm text-muted-foreground">
          Bradford-Faktor {yearFilter} für {employeeName(employeeFilter)}:{" "}
          <span className="font-semibold text-foreground tabular-nums">{bradford}</span>{" "}
          (Häufigkeit² × Krankheitstage)
        </p>
      )}

      <Card>
        <CardContent className="py-2">
          {query.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="sick-leaves"
              emptyMessage="Keine Krankmeldungen gefunden."
            />
          )}
        </CardContent>
      </Card>

      {/* Verstecktes File-Input fuer den AU-Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        className="hidden"
        onChange={onFileSelected}
      />

      <ReportSickLeaveDialog open={reportOpen} onOpenChange={setReportOpen} />
      {closeTarget && (
        <CloseSickLeaveDialog sickLeave={closeTarget} onClose={() => setCloseTarget(null)} />
      )}
    </div>
  )
}
